import React, {useState, useEffect, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import Field from '../field'
import {
    Box,
    Button,
    Checkbox,
    Text,
    Image,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Collapse, Textarea} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import {Controller} from 'react-hook-form'
import useGiftWrapFields from '../forms/useGiftWrapFields'
import {getUrl} from '../../utils/cloudinary-utils'
import {ReduceWasteIcon} from '../icons'
import useTagManager from '../../hooks/use-tag-manager.js'

const MAX_MESSAGE_LENGTH = 200

const GiftWrapBox = ({
    form,
    shipment,
    setShipmentGift,
    isCheckout,
    isCart = false,
    hideSaveButton = false
}) => {
    const styles = useMultiStyleConfig('GiftWrapBox')
    const {formatMessage} = useIntl()

    const hasExistingMessage = Boolean(shipment?.gift && shipment?.giftMessage)
    const [isSaved, setIsSaved] = useState(hasExistingMessage)
    const [isExpanded, setIsExpanded] = useState(shipment?.gift || false)
    const [charactersRemaining, setCharactersRemaining] = useState(
        MAX_MESSAGE_LENGTH - (shipment?.giftMessage?.length || 0)
    )
    const tagManager = useTagManager()
    const giftMessageInitValue = useRef('')

    const fields = useGiftWrapFields({
        form,
        setShipmentGift,
        shipment,
        isCheckout,
        setCharactersRemaining
    })

    useEffect(() => {
        if (shipment?.gift !== undefined) {
            setIsExpanded(shipment.gift)
        }
        if (shipment?.gift && shipment?.giftMessage) {
            setIsSaved(true)
        }
    }, [shipment?.gift, shipment?.giftMessage])

    const handleCheckboxChange = useCallback(
        async (checked) => {
            setIsExpanded(checked)
            if (!checked) {
                setIsSaved(false)
                form.setValue('giftWrapMessage', '')
                setCharactersRemaining(MAX_MESSAGE_LENGTH)
                setShipmentGift({giftWrapCheckBox: false})
            } else {
                setShipmentGift({giftWrapCheckBox: true})
            }
        },
        [form, setShipmentGift]
    )

    const handleSave = useCallback(
        async (formValues) => {
            await setShipmentGift(formValues)
            setIsSaved(true)
        },
        [setShipmentGift]
    )

    const handleEdit = useCallback(() => {
        setIsSaved(false)
    }, [])

    const hasErrors = Boolean(form.formState.errors?.giftWrapMessage)

    if (isCheckout) {
        return (
            <Box>
                <Field {...fields.giftWrapCheckBox} />
                <Field {...fields.giftWrapMessage} />
            </Box>
        )
    }

    return (
        <Box
            {...styles.container}
            data-testid="gift-wrap-container"
            className="gift-wrap-container"
        >
            <Box {...styles.header}>
                <Controller
                    name="giftWrapCheckBox"
                    control={form.control}
                    defaultValue={fields.giftWrapCheckBox.defaultValue}
                    render={({field: {onChange, value, ref}}) => (
                        <Checkbox
                            ref={ref}
                            isChecked={value}
                            onChange={(e) => {
                                const checked = e.target.checked
                                onChange(checked)
                                if (!hideSaveButton) {
                                    handleCheckboxChange(checked)
                                } else {
                                    setIsExpanded(checked)
                                }

                                if (checked && isCart) {
                                    tagManager.checkoutFormFieldChange(
                                        'gift_wrap_select',
                                        'Form Field Tracking',
                                        'cart gift select',
                                        'gift wrap select'
                                    )
                                }
                            }}
                            data-testid="gift-wrap-checkbox"
                        >
                            <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                gap="8px"
                                {...styles.checkboxLabel}
                            >
                                <Image
                                    src={getUrl('UIImages/images/gift.svg')}
                                    alt=""
                                    {...styles.giftIcon}
                                    data-testid="gift-wrap-icon"
                                    marginRight="4px"
                                />
                                {fields.giftWrapCheckBox.label}
                            </Box>
                        </Checkbox>
                    )}
                />
            </Box>

            <Collapse in={isExpanded} animateOpacity startingHeight={0}>
                <Box {...styles.collapseContent}>
                    {!isSaved ? (
                        <form id="gift-wrap-box" onSubmit={form.handleSubmit(handleSave)}>
                            <Box {...styles.body}>
                                <Image
                                    src={getUrl('UIImages/images/redesign/gift-default.jpg')}
                                    alt={formatMessage({
                                        defaultMessage: 'Complimentary gift box',
                                        id: 'giftwrap.image.alt'
                                    })}
                                    {...styles.giftImage}
                                    data-testid="gift-wrap-image"
                                />
                                <Text {...styles.description} data-testid="gift-wrap-description">
                                    {formatMessage({
                                        defaultMessage:
                                            'Surprise your loved ones with a special delivery, including a free gift box, personalized message, and a gift receipt.',
                                        id: 'giftwrap.description'
                                    })}
                                </Text>
                            </Box>

                            <Text {...styles.disclaimer} data-testid="gift-wrap-disclaimer">
                                {formatMessage({
                                    defaultMessage:
                                        'Please note: Gift boxes are not available for home, candle, and apparel items.',
                                    id: 'giftwrap.disclaimer'
                                })}
                            </Text>

                            <Controller
                                name="giftWrapMessage"
                                control={form.control}
                                defaultValue={fields.giftWrapMessage.defaultValue}
                                rules={fields.giftWrapMessage.rules}
                                render={({field: {onChange, onBlur, value, ref}}) => (
                                    <Box>
                                        <Textarea
                                            ref={ref}
                                            value={value}
                                            onChange={(e) => {
                                                const clamped = e.target.value.slice(
                                                    0,
                                                    MAX_MESSAGE_LENGTH
                                                )
                                                onChange(clamped)
                                                setCharactersRemaining(
                                                    MAX_MESSAGE_LENGTH - clamped.length
                                                )
                                            }}
                                            onBlur={async (e) => {
                                                onBlur()
                                                const isValid = await form.trigger(
                                                    'giftWrapMessage'
                                                )
                                                if (
                                                    e.relatedTarget &&
                                                    e.relatedTarget?.className?.indexOf(
                                                        'next-button'
                                                    ) !== -1 &&
                                                    isValid
                                                ) {
                                                    document.querySelector('.next-button').click()
                                                }
                                                if (
                                                    e.target.value !==
                                                        giftMessageInitValue.current &&
                                                    isCart
                                                ) {
                                                    tagManager.checkoutFormFieldChange(
                                                        'gift_wrap_message',
                                                        'Form Field Tracking',
                                                        'cart gift message',
                                                        'Gift Message'
                                                    )
                                                }
                                            }}
                                            onFocus={(e) => {
                                                giftMessageInitValue.current = e.target.value
                                            }}
                                            placeholder={fields.giftWrapMessage.label}
                                            maxLength={MAX_MESSAGE_LENGTH}
                                            isInvalid={hasErrors}
                                            data-testid="gift-wrap-message-input"
                                            {...styles.textarea}
                                        />
                                        {hasErrors && (
                                            <Text
                                                {...styles.errorMessage}
                                                data-testid="gift-wrap-error"
                                            >
                                                {form.formState.errors.giftWrapMessage.message}
                                            </Text>
                                        )}
                                    </Box>
                                )}
                            />

                            <Text {...styles.charCounter} data-testid="gift-wrap-char-counter">
                                {formatMessage(
                                    {
                                        defaultMessage: '{count} characters remaining.',
                                        id: 'giftwrap.char_counter'
                                    },
                                    {count: charactersRemaining}
                                )}
                            </Text>

                            <Box {...styles.ecoNote} data-testid="gift-wrap-eco-note">
                                <ReduceWasteIcon {...styles.ecoIcon} />
                                <Text as="span">
                                    {formatMessage({
                                        defaultMessage:
                                            'Help reduce waste by skipping gift packaging.',
                                        id: 'giftwrap.eco_note'
                                    })}
                                </Text>
                            </Box>

                            {!hideSaveButton && (
                                <Button
                                    type="submit"
                                    onClick={() => form.clearErrors('global')}
                                    isLoading={form.formState.isSubmitting}
                                    isDisabled={hasErrors}
                                    {...styles.saveButton}
                                    data-testid="gift-wrap-save-btn"
                                >
                                    {formatMessage({
                                        defaultMessage: 'Save Message',
                                        id: 'giftwrap.save_button'
                                    })}
                                </Button>
                            )}
                        </form>
                    ) : (
                        <Box>
                            <Text {...styles.savedMessage} data-testid="gift-wrap-saved-message">
                                {form.getValues('giftWrapMessage') || shipment?.giftMessage}
                            </Text>
                            <Button
                                onClick={handleEdit}
                                {...styles.editButton}
                                className="edit-message-btn"
                                data-testid="gift-wrap-edit-btn"
                            >
                                {formatMessage({
                                    defaultMessage: 'Edit Message',
                                    id: 'giftwrap.edit_button'
                                })}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}

GiftWrapBox.propTypes = {
    setShipmentGift: PropTypes.func,
    shipment: PropTypes.object,
    isCheckout: PropTypes.bool,
    isCart: PropTypes.bool,
    hideSaveButton: PropTypes.bool,
    form: PropTypes.shape({
        handleSubmit: PropTypes.func,
        clearErrors: PropTypes.func,
        setValue: PropTypes.func,
        getValues: PropTypes.func,
        trigger: PropTypes.func,
        watch: PropTypes.func,
        control: PropTypes.object,
        formState: PropTypes.shape({
            isSubmitting: PropTypes.bool,
            errors: PropTypes.object
        })
    }).isRequired
}

export default GiftWrapBox
